import { useState, useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import * as d3 from 'd3';
import { 
  Users, 
  CheckCircle, 
  XCircle,
  AlertCircle,
  Clock,
  Zap,
  TrendingUp,
  RefreshCw,
  Info,
  Shield,
  GitBranch,
  Hash,
} from 'lucide-react';
import { generateMockConsensusData } from '../services/mockData'
import type { SocketCloudConsensusVisualizationTypes } from '@aaimplatform/socket-cloud-types';

type ConsensusNode = SocketCloudConsensusVisualizationTypes.components['schemas']['ConsoleConsensusNode'];

interface LogEntry {
  index: number;
  term: number;
  command: string;
  timestamp: Date;
  committed: boolean;
}

interface ElectionEvent {
  id: string;
  type: 'election_started' | 'vote_requested' | 'vote_granted' | 'leader_elected' | 'heartbeat';
  timestamp: Date;
  nodeId: string;
  targetNodeId?: string;
  term: number;
  details?: string;
}

// Removed unused interface


// Role badge component
function RoleBadge({ role }: { role: ConsensusNode['role'] }) {
  const config = {
    leader: { color: 'text-yellow-400', bg: 'bg-yellow-400/20', icon: Shield },
    follower: { color: 'text-blue-400', bg: 'bg-blue-400/20', icon: Users },
    candidate: { color: 'text-purple-400', bg: 'bg-purple-400/20', icon: Zap },
    observer: { color: 'text-gray-400', bg: 'bg-gray-400/20', icon: Info },
  };
  
  const { color, bg, icon: Icon } = config[role];
  
  return (
    <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full ${bg}`}>
      <Icon className={`w-3.5 h-3.5 ${color}`} />
      <span className={`text-xs font-medium ${color} capitalize`}>{role}</span>
    </div>
  );
}

// Node status indicator
function NodeStatus({ status }: { status: ConsensusNode['status'] }) {
  const config = {
    healthy: { icon: CheckCircle, color: 'text-green-400' },
    lagging: { icon: AlertCircle, color: 'text-yellow-400' },
    disconnected: { icon: XCircle, color: 'text-red-400' },
  };
  
  const { icon: Icon, color } = config[status];
  
  return <Icon className={`w-4 h-4 ${color}`} />;
}

// Consensus visualization using D3
function ConsensusVisualization({ 
  nodes,
  onNodeSelect, 
}: { 
  nodes: ConsensusNode[];
  onNodeSelect: (node: ConsensusNode) => void;
}) {
  const svgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    if (!svgRef.current) {
return;
}

    const svg = d3.select(svgRef.current);
    const width = 600;
    const height = 400;
    const centerX = width / 2;
    const centerY = height / 2;
    const radius = 120;

    svg.selectAll('*').remove();

    const g = svg.append('g');

    // Calculate node positions in a circle
    const nodeData = nodes.map((node, i) => {
      const angle = (i * 2 * Math.PI) / nodes.length - Math.PI / 2;
      return {
        ...node,
        x: centerX + radius * Math.cos(angle),
        y: centerY + radius * Math.sin(angle),
      };
    });

    // Draw connections (heartbeats from leader)
    const leader = nodeData.find(n => n.role === 'leader');
    if (leader) {
      const connections = g.append('g').attr('class', 'connections');
      
      nodeData.forEach(node => {
        if (node.id !== leader.id && node.role !== 'observer') {
          connections.append('line')
            .attr('x1', leader.x)
            .attr('y1', leader.y)
            .attr('x2', node.x)
            .attr('y2', node.y)
            .attr('stroke', '#4b5563')
            .attr('stroke-width', 2)
            .attr('stroke-dasharray', '5,5')
            .attr('opacity', 0.5);
        }
      });
    }

    // Draw nodes
    const nodeGroups = g.selectAll('.node')
      .data(nodeData)
      .enter()
      .append('g')
      .attr('class', 'node')
      .attr('transform', d => `translate(${d.x},${d.y})`)
      .style('cursor', 'pointer')
      .on('click', (_, d) => onNodeSelect(d));

    // Node circles
    nodeGroups.append('circle')
      .attr('r', 30)
      .attr('fill', d => {
        if (d.role === 'leader') {
return '#fbbf24';
}
        if (d.role === 'candidate') {
return '#8b5cf6';
}
        if (d.role === 'observer') {
return '#6b7280';
}
        return '#3b82f6';
      })
      .attr('stroke', '#1f2937')
      .attr('stroke-width', 2);

    // Node labels
    nodeGroups.append('text')
      .attr('text-anchor', 'middle')
      .attr('dy', -40)
      .attr('fill', '#e5e7eb')
      .attr('font-size', '14px')
      .attr('font-weight', 'bold')
      .text(d => d.name);

    // Node status icons
    nodeGroups.append('text')
      .attr('text-anchor', 'middle')
      .attr('dy', 5)
      .attr('fill', '#1f2937')
      .attr('font-size', '20px')
      .text(d => {
        if (d.role === 'leader') {
return '👑';
}
        if (d.role === 'candidate') {
return '🗳️';
}
        if (d.role === 'observer') {
return '👁️';
}
        return '📋';
      });

    // Term labels
    nodeGroups.append('text')
      .attr('text-anchor', 'middle')
      .attr('dy', 50)
      .attr('fill', '#9ca3af')
      .attr('font-size', '12px')
      .text(d => `Term ${d.term}`);

    // Add pulsing animation for leader
    const leaderNode = nodeGroups.filter(d => d.role === 'leader');
    if (leaderNode.node()) {
      const pulse = leaderNode.append('circle')
        .attr('r', 30)
        .attr('fill', 'none')
        .attr('stroke', '#fbbf24')
        .attr('stroke-width', 2);

      function animatePulse() {
        pulse
          .attr('r', 30)
          .attr('opacity', 1)
          .transition()
          .duration(1000)
          .attr('r', 45)
          .attr('opacity', 0)
          .on('end', animatePulse);
      }
      animatePulse();
    }

  }, [nodes, onNodeSelect]);

  return (
    <svg
      ref={svgRef}
      width="100%"
      height="400"
      viewBox="0 0 600 400"
      className="w-full"
    />
  );
}

// Log replication visualization
function LogReplicationView({ nodes }: { nodes: ConsensusNode[] }) {
  const leader = nodes.find(n => n.role === 'leader');
  if (!leader) {
return null;
}

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-white mb-3">Log Replication Status</h3>
      
      {nodes.map(node => (
        <div key={node.id} className="bg-gray-800 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <span className="font-medium text-white">{node.name}</span>
              <RoleBadge role={node.role} />
              <NodeStatus status={node.status} />
            </div>
            <div className="text-sm text-gray-400">
              Commit Index: {node.commitIndex} / Applied: {node.lastApplied}
            </div>
          </div>

          {/* Log entries visualization */}
          <div className="flex gap-1">
            {(node.log ?? []).slice(-20).map((entry, i) => (
              <div
                key={i}
                className={`w-4 h-8 rounded ${
                  entry.committed ? 'bg-green-500' : 'bg-gray-600'
                } ${entry.index <= node.lastApplied ? 'opacity-100' : 'opacity-50'}`}
                title={`Index: ${entry.index}, Term: ${entry.term}, ${entry.committed ? 'Committed' : 'Uncommitted'}`}
              />
            ))}
          </div>

          {/* Progress bar */}
          <div className="mt-2 flex items-center gap-2 text-xs">
            <div className="flex-1 bg-gray-700 rounded-full h-2">
              <div
                className="bg-green-500 h-2 rounded-full transition-all"
                style={{ width: `${(node.commitIndex / (leader.log ?? []).length) * 100}%` }}
              />
            </div>
            <span className="text-gray-400">{Math.round((node.commitIndex / (leader.log ?? []).length) * 100)}%</span>
          </div>
        </div>
      ))}
    </div>
  );
}

// Election timeline
function ElectionTimeline({ events }: { events: ElectionEvent[] }) {
  return (
    <div className="space-y-2">
      {events.slice(0, 10).map((event, i) => (
        <motion.div
          key={event.id}
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: i * 0.05 }}
          className="flex items-start gap-3 text-sm"
        >
          <div className="text-gray-500 text-xs w-20 flex-shrink-0">
            {event.timestamp.toLocaleTimeString()}
          </div>
          <div className={`w-2 h-2 rounded-full flex-shrink-0 mt-1.5 ${
            event.type === 'leader_elected' ? 'bg-yellow-400' :
            event.type === 'election_started' ? 'bg-purple-400' :
            event.type === 'vote_granted' ? 'bg-green-400' :
            'bg-gray-400'
          }`} />
          <div className="flex-1">
            <div className="text-white">
              {event.type.replace(/_/g, ' ').charAt(0).toUpperCase() + event.type.replace(/_/g, ' ').slice(1)}
            </div>
            <div className="text-gray-400 text-xs">
              Node: {event.nodeId}
              {event.targetNodeId && ` → ${event.targetNodeId}`}
              {event.details && ` • ${event.details}`}
            </div>
          </div>
        </motion.div>
      ))}
    </div>
  );
}

// Main Consensus page component
export function ConsensusPage() {
  const [consensusData, setConsensusData] = useState<{
    nodes: ConsensusNode[];
    events: ElectionEvent[];
    metrics: {
      totalElections: number;
      failedElections: number;
      averageElectionTime: number;
      throughput: number;
      electionTimeout: number;
      heartbeatInterval: number;
      averageCommitLatency: number;
    };
  }>(generateMockConsensusData());
  const [selectedNode, setSelectedNode] = useState<ConsensusNode | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);

  // Simulate real-time updates
  useEffect(() => {
    if (!autoRefresh) {
return;
}

    const interval = setInterval(() => {
      setConsensusData(prevData => {
        // Simulate heartbeats
        const newNodes = prevData.nodes.map(node => ({
          ...node,
          lastHeartbeat: node.role === 'leader' || Math.random() > 0.3
            ? new Date().toISOString()
            : node.lastHeartbeat,
        }));

        // Simulate new log entries
        if (Math.random() > 0.7) {
          const leader = newNodes.find(n => n.role === 'leader');
          if (leader) {
            const newLogEntry: SocketCloudConsensusVisualizationTypes.components['schemas']['ConsoleConsensusLogEntry'] = {
              index: (leader.log ?? []).length + 1,
              term: leader.term,
              command: `SET key${(leader.log ?? []).length} value${(leader.log ?? []).length}`,
              timestamp: new Date().toISOString(),
              committed: false,
            };
            (leader.log ?? []).push(newLogEntry);

            // Replicate to followers with delay
            setTimeout(() => {
              newNodes.forEach(node => {
                if (node.role === 'follower' && node.status === 'healthy') {
                  (node.log ?? []).push({ ...newLogEntry });
                }
              });
            }, 500);
          }
        }

        // Simulate new events
        const newEvents = [...prevData.events];
        if (Math.random() > 0.8) {
          newEvents.unshift({
            id: `e${Date.now()}`,
            type: 'heartbeat',
            timestamp: new Date(),
            nodeId: 'node-1',
            term: 5,
          });
        }

        return {
          ...prevData,
          nodes: newNodes,
          events: newEvents.slice(0, 50),
        };
      });
    }, 2000);

    return () => clearInterval(interval);
  }, [autoRefresh]);

  const handleRefresh = useCallback(() => {
    setConsensusData(generateMockConsensusData());
  }, []);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-white">Consensus Monitoring</h1>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="rounded"
            />
            <span className="text-gray-300 text-sm">Auto-refresh</span>
          </label>
          <button
            onClick={handleRefresh}
            className="btn-secondary flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* Metrics overview */}
      <div className="dashboard-grid">
        <div className="bg-gray-900 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-400 text-sm">Current Term</span>
            <Hash className="w-5 h-5 text-gray-400" />
          </div>
          <div className="text-2xl font-bold text-white">
            {consensusData.nodes[0]?.term || 0}
          </div>
        </div>
        
        <div className="bg-gray-900 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-400 text-sm">Elections</span>
            <GitBranch className="w-5 h-5 text-purple-400" />
          </div>
          <div className="text-2xl font-bold text-purple-400">
            {consensusData.metrics.totalElections}
          </div>
          <div className="text-xs text-gray-500 mt-1">
            {consensusData.metrics.failedElections} failed
          </div>
        </div>
        
        <div className="bg-gray-900 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-400 text-sm">Avg Election Time</span>
            <Clock className="w-5 h-5 text-yellow-400" />
          </div>
          <div className="text-2xl font-bold text-yellow-400">
            {consensusData.metrics.averageElectionTime}ms
          </div>
        </div>
        
        <div className="bg-gray-900 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-400 text-sm">Throughput</span>
            <TrendingUp className="w-5 h-5 text-green-400" />
          </div>
          <div className="text-2xl font-bold text-green-400">
            {consensusData.metrics.throughput}
          </div>
          <div className="text-xs text-gray-500 mt-1">ops/sec</div>
        </div>
      </div>

      {/* Main content grid */}
      <div className="feature-grid">
        {/* Consensus visualization */}
        <div className="bg-gray-900 rounded-lg p-6">
          <h2 className="text-xl font-semibold text-white mb-4">Cluster State</h2>
          <ConsensusVisualization
            nodes={consensusData.nodes}
            onNodeSelect={setSelectedNode}
          />
          
          {/* Node details */}
          {selectedNode && (
            <div className="mt-4 p-4 bg-gray-800 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-medium text-white">{selectedNode.name}</h3>
                <button
                  onClick={() => setSelectedNode(null)}
                  className="text-gray-400 hover:text-white"
                >
                  <XCircle className="w-4 h-4" />
                </button>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-400">Role</span>
                  <RoleBadge role={selectedNode.role} />
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Term</span>
                  <span className="text-white">{selectedNode.term}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Voted For</span>
                  <span className="text-white">{selectedNode.votedFor || 'None'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Last Heartbeat</span>
                  <span className="text-white">
                    {new Date(selectedNode.lastHeartbeat).toLocaleTimeString()}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Election timeline */}
        <div className="bg-gray-900 rounded-lg p-6">
          <h2 className="text-xl font-semibold text-white mb-4">Election Timeline</h2>
          <div className="max-h-96 overflow-y-auto">
            <ElectionTimeline events={consensusData.events} />
          </div>
        </div>
      </div>

      {/* Log replication status */}
      <div className="bg-gray-900 rounded-lg p-6">
        <LogReplicationView nodes={consensusData.nodes} />
      </div>

      {/* Configuration */}
      <div className="bg-gray-900 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Consensus Configuration</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6">
          <div>
            <label className="block text-sm text-gray-400 mb-1">Election Timeout</label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                value={consensusData.metrics.electionTimeout}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded text-white"
                readOnly
              />
              <span className="text-gray-400 text-sm">ms</span>
            </div>
          </div>
          <div>
            <label className="block text-sm text-gray-400 mb-1">Heartbeat Interval</label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                value={consensusData.metrics.heartbeatInterval}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded text-white"
                readOnly
              />
              <span className="text-gray-400 text-sm">ms</span>
            </div>
          </div>
          <div>
            <label className="block text-sm text-gray-400 mb-1">Commit Latency</label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                value={consensusData.metrics.averageCommitLatency}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded text-white"
                readOnly
              />
              <span className="text-gray-400 text-sm">ms</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ConsensusPage;